<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import { useSocialCommentsStore } from 'dashboard/stores/socialComments';
import { useSocialPostsStore } from 'dashboard/stores/socialPosts';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  // Enriched by PostsIndex with `pageName` (the Facebook page's name) and
  // `isScheduled` alongside the raw SocialPost fields.
  post: { type: Object, default: null },
  isScheduled: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
  // Whether the account can actually reach the composer right now (post_publishing
  // enabled) -- the empty state's action shouldn't point at a button the
  // account doesn't have.
  canCreatePost: { type: Boolean, default: false },
});

const emit = defineEmits(['cancel', 'create']);

const { t } = useI18n();
const { isAdmin } = useAdmin();

const commentsStore = useSocialCommentsStore();
const { threadRecords, uiFlags } = storeToRefs(commentsStore);

const isEmpty = computed(() => !props.isLoading && !props.post);

const relativeTime = value => {
  if (!value) return t('COMMENTS.LIST.NO_DATE');
  return formatDistanceToNow(parseISO(value), { addSuffix: true });
};

const subLabel = computed(() => {
  if (!props.post) return '';
  const time = relativeTime(props.post.postedAt);
  return props.isScheduled
    ? t('COMMENTS.POSTS.SCHEDULED_META', { time })
    : t('COMMENTS.POSTS.POSTED_META', { time });
});

const scheduledCalloutTime = computed(() => {
  if (!props.post) return '';
  return relativeTime(props.post.postedAt);
});

const initials = name => (name || '?').trim().charAt(0).toUpperCase();

// A deleted comment is gone from Facebook but deliberately kept here, so the record of
// what was said and who removed it survives. It stays visible in the thread, struck
// through and stripped of every action -- hide, like and reply would all fail against
// Graph, and offering them would suggest the comment is still there.
const isDeleted = comment => comment.status === 'deleted';

// The design gives each commenter their own avatar tint so a busy thread is scannable
// by face rather than by reading every name. Derived from the author's name, not from
// their position in the list, so one person keeps the same colour as the thread grows.
const AVATAR_TINTS = [
  'bg-n-teal-3 text-n-teal-11',
  'bg-n-amber-3 text-n-amber-11',
  'bg-n-brand/20 text-n-blue-11',
  'bg-n-solid-2 text-n-slate-11',
];
const avatarTint = name => {
  const key = (name || '').trim();
  let sum = 0;
  for (let i = 0; i < key.length; i += 1) sum += key.charCodeAt(i);
  return AVATAR_TINTS[sum % AVATAR_TINTS.length];
};

// Faster than the queue's 30s cadence: this pane is where an agent watches a thread
// they have just replied to, so a stale minute is far more noticeable here.
const THREAD_POLL_INTERVAL_MS = 15000;
let pollTimer = null;

const replyingId = ref(null);
const replyText = ref('');

// This pane is the reuse target the store's fetchThread(socialPostId) was built for --
// same call CommentDetail makes for the comments queue's detail pane, kept out of
// `records`/`meta` so it never clobbers whatever the agent is filtering by elsewhere.
// A scheduled post hasn't gone out yet, so it cannot have comments -- skip the fetch
// entirely rather than asking the Graph API for a thread that can only ever be empty.
const loadThread = () => {
  if (!props.post?.id || props.isScheduled) return Promise.resolve();
  return commentsStore.fetchThread(props.post.id).catch(() => {});
};

watch(
  () => [props.post?.id, props.isScheduled],
  () => {
    commentsStore.clearThread();
    replyingId.value = null;
    loadThread();
  },
  { immediate: true }
);

// New visitor comments and the page's own replies both reach our database only when
// Meta's `feed` webhook lands, which is nothing this pane can learn from its own
// request. Without a poll the thread stayed frozen on whatever it fetched when the
// post was selected, and every new comment needed a page reload to appear.
onMounted(() => {
  pollTimer = setInterval(loadThread, THREAD_POLL_INTERVAL_MS);
});

onUnmounted(() => {
  clearInterval(pollTimer);
});

// Facebook only lets a post's message be edited once it exists -- the image, video or link
// is fixed -- so this edits the caption in place rather than reopening the composer, which
// would offer attachment controls that cannot take effect. A still-scheduled post can also
// be moved, which is the one other thing Graph allows.
const postsStore = useSocialPostsStore();
const isEditing = ref(false);
const draftMessage = ref('');
const draftScheduledAt = ref('');
const isSavingEdit = ref(false);

const toDatetimeLocal = date => {
  const pad = value => String(value).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const startEdit = () => {
  draftMessage.value = props.post?.message || '';
  draftScheduledAt.value =
    props.isScheduled && props.post?.postedAt
      ? toDatetimeLocal(parseISO(props.post.postedAt))
      : '';
  isEditing.value = true;
};

const cancelEdit = () => {
  isEditing.value = false;
};

const canSaveEdit = computed(
  () => Boolean(draftMessage.value.trim()) && !isSavingEdit.value
);

const saveEdit = async () => {
  if (!canSaveEdit.value) return;

  isSavingEdit.value = true;
  try {
    await postsStore.update({
      id: props.post.id,
      message: draftMessage.value.trim(),
      scheduledAt: draftScheduledAt.value
        ? new Date(draftScheduledAt.value).toISOString()
        : null,
    });
    isEditing.value = false;
  } catch (error) {
    useAlert(error.message || t('COMMENTS.POSTS.EDIT.ERROR'));
  } finally {
    isSavingEdit.value = false;
  }
};

// Selecting a different post must not carry a half-finished edit across to it.
watch(
  () => props.post?.id,
  () => {
    isEditing.value = false;
  }
);

const cancelDialogRef = ref(null);
const openCancelDialog = () => cancelDialogRef.value?.open();
const confirmCancel = () => {
  emit('cancel', props.post.id);
  cancelDialogRef.value?.close();
};

// The comments API returns a flat list of sibling rows rather than a nested reply tree,
// so the thread is rebuilt here by matching `parentExternalId` back to a parent that is
// actually present. Replies are keyed by parent rather than filtered per comment so a
// long thread costs one pass instead of one pass per top-level comment.
//
// Every reply nests, not just the page's own. Filtering these to `fromPage` meant a
// visitor answering another visitor was treated as a new top-level comment: it jumped
// out of the exchange it belonged to and was rendered as though it opened a new one.
const byParent = computed(() => {
  const map = new Map();
  threadRecords.value.forEach(record => {
    const parent = record.parentExternalId;
    if (!parent) return;
    if (!map.has(parent)) map.set(parent, []);
    map.get(parent).push(record);
  });
  map.forEach(replies =>
    replies.sort((a, b) =>
      (a.commentedAt || '').localeCompare(b.commentedAt || '')
    )
  );
  return map;
});

const repliesFor = comment => byParent.value.get(comment.externalId) || [];

// A comment is top-level when nothing it points at is in this thread -- either it has no
// parent, or its parent is the post itself, which never appears as a record.
const topLevelComments = computed(() => {
  const present = new Set(threadRecords.value.map(record => record.externalId));
  return threadRecords.value.filter(
    record => !record.parentExternalId || !present.has(record.parentExternalId)
  );
});

// Which comment's `...` menu is open. Hide and delete live behind it rather than sitting
// on the row: delete cannot be undone on Facebook, and giving it the same weight as Reply
// on every comment put an irreversible action one stray click away.
const openMenuId = ref(null);
const toggleMenu = comment => {
  openMenuId.value = openMenuId.value === comment.id ? null : comment.id;
};
const closeMenu = () => {
  openMenuId.value = null;
};

// `post.commentCount` is only as fresh as the last posts fetch, so it sat one behind
// the list right below it every time the poll brought in a new comment. Once the
// thread has loaded it is the authoritative answer for what this pane is rendering.
// Replies count towards the total, the same way Facebook's own figure does, so this
// number agrees with `post.commentCount` rather than quietly reporting a smaller one.
// Replies count towards the total the way Facebook's own figure does, but deleted rows
// do not: they are kept locally for the audit trail and no longer exist on Facebook, so
// counting them puts this number above both `post.commentCount` and the list beside it.
const commentCount = computed(() => {
  if (!threadRecords.value.length) return props.post?.commentCount;
  return threadRecords.value.filter(record => !isDeleted(record)).length;
});

// Every one of these is a Graph write that can fail -- an expired page token, or a comment
// Facebook has removed since the thread was fetched. The store rethrows Meta's own message,
// so surface that rather than leaving the row looking as though nothing happened.
const runAction = async action => {
  try {
    await action();
  } catch (error) {
    useAlert(error.message || t('COMMENTS.ACTIONS.ERROR'));
  }
};

const handleHide = comment => runAction(() => commentsStore.hide(comment.id));
const handleUnhide = comment =>
  runAction(() => commentsStore.unhide(comment.id));
// One control, not a Like/Unlike pair: `likedByPage` is now known, so the button can show
// the page's own state and the real like count instead of offering both directions blindly.
const toggleLike = comment =>
  runAction(() =>
    comment.likedByPage
      ? commentsStore.unlike(comment.id)
      : commentsStore.like(comment.id)
  );

const startReply = comment => {
  replyingId.value = comment.id;
  replyText.value = '';
};
const cancelReply = () => {
  replyingId.value = null;
  replyText.value = '';
};

// Mirrors CommentReplyBox's own contract: the typed text survives a failed
// send so a Graph rejection never costs the agent what they wrote.
//
// `isSendingReply` is what stops a double post. The Graph call takes long enough that a
// second Enter (or a click on the button while Enter is still in flight) arrived before
// the first cleared `replyText`, so the same text was published to Facebook twice --
// two real comments on the page, not a display artefact. The store's own `replying`
// flag is shared across every comment in the thread, so it cannot be used here.
const isSendingReply = ref(false);

const sendReply = async comment => {
  const message = replyText.value.trim();
  if (!message || isSendingReply.value) return;

  isSendingReply.value = true;
  try {
    // The store puts the reply straight into the thread from the send response, so there is
    // no refetch here: the one that used to follow raced its own optimistic insert, and
    // read as the reply appearing, vanishing, then coming back seconds later. The poll
    // above still reconciles the row against whatever Meta's webhook eventually delivers.
    await commentsStore.reply({ id: comment.id, message });
    replyingId.value = null;
    replyText.value = '';
  } catch (error) {
    useAlert(error.message || t('COMMENTS.REPLY_BOX.ERROR'));
  } finally {
    isSendingReply.value = false;
  }
};

// Deleting is the one comment action Facebook cannot undo, so it goes through a
// confirmation rather than straight off the row like hide or like.
const deleteDialogRef = ref(null);
const pendingDeleteId = ref(null);

const openDeleteDialog = comment => {
  pendingDeleteId.value = comment.id;
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  const id = pendingDeleteId.value;
  deleteDialogRef.value?.close();
  pendingDeleteId.value = null;
  if (!id) return;

  await runAction(() => commentsStore.delete(id));
  // The store drops the row from `records`, but this pane renders `threadRecords` --
  // refetch so the deleted comment leaves the thread too.
  loadThread();
};
</script>

<template>
  <div class="flex flex-col w-full h-full">
    <div
      v-if="isEmpty"
      data-testid="post-detail-empty"
      class="flex flex-col items-center justify-center flex-1 gap-3 p-6 text-center"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('COMMENTS.POSTS.EMPTY_TITLE') }}
      </p>
      <Button
        v-if="canCreatePost"
        data-testid="post-detail-empty-action"
        size="sm"
        variant="faded"
        color="slate"
        :label="t('COMMENTS.POSTS.EMPTY_ACTION')"
        @click="emit('create')"
      />
      <p v-else class="max-w-xs text-sm text-n-slate-11">
        {{ t('COMMENTS.POSTS.EMPTY_SUBTITLE') }}
      </p>
    </div>
    <div
      v-else-if="!post"
      data-testid="post-detail-loading"
      class="flex flex-col w-full gap-3 p-6"
    >
      <div class="w-2/3 h-4 rounded animate-pulse bg-n-solid-2" />
      <div class="w-full h-24 rounded animate-pulse bg-n-solid-2" />
    </div>
    <template v-else>
      <div class="w-full max-w-[680px] px-6 pt-6 pb-8 mx-auto">
        <div class="flex items-center gap-3 mb-[18px]">
          <span
            class="grid text-base font-semibold rounded-full size-10 shrink-0 bg-n-solid-2 text-n-slate-11 place-items-center"
          >
            {{ initials(post.pageName) }}
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-[15px] font-bold truncate text-n-slate-12">
              {{ post.pageName }}
            </p>
            <p
              data-testid="post-detail-time"
              class="text-[12.5px] text-n-slate-10"
            >
              {{ subLabel }}
            </p>
          </div>
          <button
            v-if="canCreatePost && !isEditing"
            type="button"
            data-testid="edit-post-button"
            class="h-8 px-3 text-sm font-medium border rounded-lg shrink-0 border-n-weak bg-n-solid-1 text-n-slate-12 hover:bg-n-alpha-1"
            @click="startEdit"
          >
            {{ t('COMMENTS.POSTS.EDIT.BUTTON') }}
          </button>
          <button
            v-if="isScheduled && isAdmin"
            type="button"
            data-testid="cancel-post-button"
            class="h-8 px-3 text-sm font-medium border rounded-lg shrink-0 border-n-weak text-n-slate-12 hover:bg-n-alpha-1"
            @click="openCancelDialog"
          >
            {{ t('COMMENTS.POSTS.CANCEL') }}
          </button>
          <span
            data-testid="post-row-status"
            class="px-2.5 py-1 text-[10px] font-medium uppercase tracking-wide rounded-full shrink-0"
            :class="
              isScheduled
                ? 'bg-n-amber-3 text-n-amber-11'
                : 'bg-n-teal-3 text-n-teal-11'
            "
          >
            {{
              isScheduled
                ? t('COMMENTS.POSTS.FILTERS.SCHEDULED')
                : t('COMMENTS.POSTS.FILTERS.PUBLISHED')
            }}
          </span>
        </div>

        <div v-if="isEditing" class="flex flex-col gap-2 mb-4">
          <textarea
            v-model="draftMessage"
            data-testid="post-edit-message"
            rows="5"
            class="w-full p-3 text-[16.5px] leading-[1.6] border resize-none rounded-xl border-n-weak bg-n-solid-1 text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand focus-visible:-outline-offset-1"
          />
          <div v-if="isScheduled" class="flex flex-col gap-1">
            <label for="post-edit-schedule" class="text-xs text-n-slate-11">
              {{ t('COMMENTS.POSTS.EDIT.SCHEDULE_LABEL') }}
            </label>
            <input
              id="post-edit-schedule"
              v-model="draftScheduledAt"
              type="datetime-local"
              data-testid="post-edit-schedule"
              class="h-9 px-3 text-sm border rounded-lg w-fit border-n-weak bg-n-solid-1 text-n-slate-12"
            />
          </div>
          <p class="text-xs text-n-slate-10">
            {{ t('COMMENTS.POSTS.EDIT.ATTACHMENT_NOTE') }}
          </p>
          <div class="flex items-center gap-2">
            <Button
              data-testid="post-edit-save"
              size="sm"
              :label="t('COMMENTS.POSTS.EDIT.SAVE')"
              :disabled="!canSaveEdit"
              :is-loading="isSavingEdit"
              @click="saveEdit"
            />
            <Button
              data-testid="post-edit-cancel"
              size="sm"
              variant="faded"
              color="slate"
              :label="t('COMMENTS.POSTS.EDIT.CANCEL')"
              @click="cancelEdit"
            />
          </div>
        </div>
        <p
          v-else
          data-testid="post-detail-message"
          class="mb-4 text-[16.5px] leading-[1.6] whitespace-pre-wrap text-n-slate-12"
        >
          {{ post.message || t('COMMENTS.POSTS.NO_MESSAGE') }}
        </p>

        <img
          v-if="post.pictureUrl"
          :src="post.pictureUrl"
          alt=""
          data-testid="post-detail-image"
          class="object-cover w-full mb-3 max-h-[420px] rounded-lg bg-n-solid-2"
        />

        <a
          v-if="!isScheduled && post.permalink"
          :href="post.permalink"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="post-permalink"
          class="inline-flex items-center gap-1 mb-4 text-sm text-n-blue-9 hover:underline"
        >
          {{ t('COMMENTS.POSTS.VIEW_POST') }}
          <Icon icon="i-lucide-external-link" class="size-3.5" />
        </a>

        <div
          v-if="isScheduled"
          data-testid="post-detail-scheduled-callout"
          class="flex items-center gap-[13px] px-[18px] py-[15px] rounded-[20px] bg-n-amber-3"
        >
          <Icon
            icon="i-lucide-clock"
            class="text-n-amber-11 size-[19px] shrink-0"
          />
          <div>
            <p class="text-sm font-bold text-n-amber-11">
              {{
                t('COMMENTS.POSTS.SCHEDULED_CALLOUT.TITLE', {
                  time: scheduledCalloutTime,
                })
              }}
            </p>
            <p class="text-[12.5px] text-n-amber-11/80">
              {{ t('COMMENTS.POSTS.SCHEDULED_CALLOUT.BODY') }}
            </p>
          </div>
        </div>

        <template v-else>
          <!-- Facebook's own summary line: the reaction tally on the left, the counts it
               cannot show as reactions on the right. It replaced three large tiles that
               spent a third of the pane rendering three single-digit numbers. -->
          <div
            data-testid="post-detail-stats"
            class="flex items-center justify-between gap-3 py-2 text-[13px] text-n-slate-11"
          >
            <span v-if="post.likeCount" class="flex items-center gap-1.5">
              <span
                class="grid rounded-full size-[18px] bg-n-ruby-9 place-items-center"
              >
                <Icon
                  icon="i-lucide-heart"
                  class="text-white size-2.5 fill-current"
                />
              </span>
              {{ post.likeCount }}
            </span>
            <span v-else />
            <span class="flex items-center gap-1.5">
              <span data-testid="post-detail-comment-count">
                {{ t('COMMENTS.POSTS.COMMENT_COUNT', { count: commentCount }) }}
              </span>
              <span aria-hidden="true">·</span>
              <span>
                {{
                  t('COMMENTS.POSTS.STATS.SHARE_COUNT', {
                    count: post.shareCount || 0,
                  })
                }}
              </span>
            </span>
          </div>
          <div class="h-px bg-n-weak" />
          <h4
            data-testid="post-detail-comments-heading"
            class="py-3 text-[13px] font-semibold text-n-slate-11"
          >
            {{ t('COMMENTS.POSTS.COMMENTS_HEADING') }}
          </h4>

          <p
            v-if="!topLevelComments.length && !uiFlags.fetchingThread"
            data-testid="post-detail-no-comments"
            class="text-[13.5px] text-n-slate-10"
          >
            {{ t('COMMENTS.POSTS.NO_COMMENTS') }}
          </p>

          <div class="flex flex-col gap-3">
            <div
              v-for="comment in topLevelComments"
              :key="comment.id"
              data-testid="post-detail-comment"
              class="flex gap-2"
            >
              <span
                class="grid text-xs font-bold rounded-full size-8 shrink-0 place-items-center"
                :class="avatarTint(comment.authorName)"
              >
                {{ initials(comment.authorName) }}
              </span>
              <div class="flex-1 min-w-0">
                <div class="flex items-start gap-1 group">
                  <!-- The bubble hugs its text rather than filling the row, so a
                       three-word comment reads as a three-word comment. -->
                  <div class="relative max-w-full">
                    <div
                      class="px-3 py-2 rounded-2xl bg-n-alpha-2"
                      :class="{
                        'opacity-60': comment.status === 'hidden',
                        'opacity-50': isDeleted(comment),
                      }"
                    >
                      <div class="flex items-center gap-1.5">
                        <span class="text-[13px] font-semibold text-n-slate-12">
                          {{ comment.authorName }}
                        </span>
                        <span
                          v-if="comment.fromPage"
                          data-testid="page-comment-badge"
                          class="px-1.5 text-[9px] font-semibold uppercase tracking-wide rounded-full shrink-0 bg-n-alpha-3 text-n-slate-11"
                        >
                          {{ t('COMMENTS.POSTS.THREAD.PAGE_TAG') }}
                        </span>
                        <span
                          v-if="isDeleted(comment)"
                          data-testid="comment-deleted-tag"
                          class="px-1.5 text-[9px] font-semibold rounded-full shrink-0 bg-n-solid-1 text-n-ruby-9"
                        >
                          {{ t('COMMENTS.POSTS.THREAD.DELETED_TAG') }}
                        </span>
                        <span
                          v-else-if="comment.status === 'hidden'"
                          data-testid="comment-hidden-tag"
                          class="px-1.5 text-[9px] font-semibold rounded-full shrink-0 bg-n-solid-1 text-n-slate-10"
                        >
                          {{ t('COMMENTS.POSTS.THREAD.HIDDEN_TAG') }}
                        </span>
                      </div>
                      <p
                        class="text-[15px] leading-[1.35] text-n-slate-12"
                        :class="{ 'line-through': isDeleted(comment) }"
                      >
                        {{ comment.message }}
                      </p>
                    </div>
                    <!-- Facebook hangs the like tally off the bubble's corner rather
                         than spending a row on it. -->
                    <span
                      v-if="comment.likeCount && !isDeleted(comment)"
                      data-testid="comment-like-count"
                      class="absolute -bottom-1.5 -right-1 inline-flex items-center gap-0.5 px-1.5 py-px text-[11px] border rounded-full border-n-weak bg-n-solid-1 text-n-slate-11"
                    >
                      <Icon
                        icon="i-lucide-heart"
                        class="size-2.5 text-n-ruby-9 fill-current"
                      />
                      {{ comment.likeCount }}
                    </span>
                  </div>

                  <div v-if="!isDeleted(comment)" class="relative shrink-0">
                    <button
                      type="button"
                      data-testid="comment-menu-toggle"
                      :aria-label="t('COMMENTS.ACTIONS.MORE')"
                      :aria-expanded="openMenuId === comment.id"
                      class="grid mt-1 rounded-full opacity-0 size-7 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 place-items-center group-hover:opacity-100 focus-visible:opacity-100"
                      :class="{ 'opacity-100': openMenuId === comment.id }"
                      @click="toggleMenu(comment)"
                    >
                      <Icon icon="i-lucide-more-horizontal" class="size-4" />
                    </button>
                    <div
                      v-if="openMenuId === comment.id"
                      class="absolute right-0 z-20 flex flex-col w-40 py-1 border shadow-lg top-9 rounded-xl border-n-weak bg-n-solid-1"
                    >
                      <button
                        type="button"
                        data-testid="comment-hide-toggle"
                        :disabled="uiFlags.hiding || uiFlags.unhiding"
                        class="flex items-center gap-2 px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-50"
                        @click="
                          closeMenu();
                          comment.status === 'hidden'
                            ? handleUnhide(comment)
                            : handleHide(comment);
                        "
                      >
                        <Icon
                          :icon="
                            comment.status === 'hidden'
                              ? 'i-lucide-eye'
                              : 'i-lucide-eye-off'
                          "
                          class="size-3.5"
                        />
                        {{
                          comment.status === 'hidden'
                            ? t('COMMENTS.ACTIONS.UNHIDE')
                            : t('COMMENTS.ACTIONS.HIDE')
                        }}
                      </button>
                      <button
                        type="button"
                        data-testid="comment-delete-button"
                        :disabled="uiFlags.deletingItem"
                        class="flex items-center gap-2 px-3 py-2 text-sm text-left text-n-ruby-9 hover:bg-n-alpha-1 disabled:opacity-50"
                        @click="
                          closeMenu();
                          openDeleteDialog(comment);
                        "
                      >
                        <Icon icon="i-lucide-trash-2" class="size-3.5" />
                        {{ t('COMMENTS.ACTIONS.DELETE') }}
                      </button>
                    </div>
                  </div>
                </div>

                <p
                  v-if="isDeleted(comment)"
                  data-testid="comment-deleted-notice"
                  class="pt-1 pl-3 text-[11px] text-n-slate-10"
                >
                  {{ t('COMMENTS.ACTIONS.DELETED_NOTICE') }}
                </p>
                <!-- Plain text links, the way Facebook sets them: the row under a comment
                     is navigation between replies, not a toolbar competing with them. -->
                <div
                  v-else
                  class="flex items-center gap-3 pt-1 pl-3 text-[12px] font-semibold text-n-slate-11"
                >
                  <button
                    type="button"
                    data-testid="comment-like-button"
                    :disabled="uiFlags.liking || uiFlags.unliking"
                    :aria-pressed="comment.likedByPage"
                    class="hover:underline disabled:opacity-50"
                    :class="{ 'text-n-ruby-9': comment.likedByPage }"
                    @click="toggleLike(comment)"
                  >
                    {{ t('COMMENTS.ACTIONS.LIKE') }}
                  </button>
                  <button
                    type="button"
                    data-testid="comment-reply-toggle"
                    class="hover:underline"
                    @click="
                      replyingId === comment.id
                        ? cancelReply()
                        : startReply(comment)
                    "
                  >
                    {{ t('COMMENTS.REPLY_BOX.REPLY_BUTTON') }}
                  </button>
                  <span class="font-normal text-n-slate-10">
                    {{ relativeTime(comment.commentedAt) }}
                  </span>
                </div>

                <div
                  v-for="reply in repliesFor(comment)"
                  :key="reply.id"
                  data-testid="post-detail-reply"
                  class="flex gap-2 mt-2"
                >
                  <span
                    class="grid text-[10px] font-bold rounded-full size-7 shrink-0 place-items-center"
                    :class="
                      reply.fromPage
                        ? 'bg-n-brand text-white'
                        : avatarTint(reply.authorName)
                    "
                  >
                    {{ initials(reply.authorName) }}
                  </span>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-start gap-1 group">
                      <div class="relative max-w-full">
                        <div
                          class="px-3 py-2 rounded-2xl bg-n-alpha-2"
                          :class="{
                            'opacity-60': reply.status === 'hidden',
                            'opacity-50': isDeleted(reply),
                          }"
                        >
                          <div class="flex items-center gap-1.5">
                            <span
                              class="text-[12.5px] font-semibold truncate text-n-slate-12"
                            >
                              {{ reply.authorName }}
                            </span>
                            <span
                              v-if="reply.fromPage"
                              data-testid="page-reply-badge"
                              class="px-1.5 text-[9px] font-semibold uppercase tracking-wide rounded-full shrink-0 bg-n-alpha-3 text-n-slate-11"
                            >
                              {{ t('COMMENTS.POSTS.THREAD.PAGE_TAG') }}
                            </span>
                            <span
                              v-if="isDeleted(reply)"
                              data-testid="reply-deleted-tag"
                              class="px-1.5 text-[9px] font-semibold rounded-full shrink-0 bg-n-solid-1 text-n-ruby-9"
                            >
                              {{ t('COMMENTS.POSTS.THREAD.DELETED_TAG') }}
                            </span>
                          </div>
                          <p
                            class="text-[14px] leading-[1.35] text-n-slate-12"
                            :class="{ 'line-through': isDeleted(reply) }"
                          >
                            {{ reply.message }}
                          </p>
                        </div>
                        <span
                          v-if="reply.likeCount && !isDeleted(reply)"
                          class="absolute -bottom-1.5 -right-1 inline-flex items-center gap-0.5 px-1.5 py-px text-[11px] border rounded-full border-n-weak bg-n-solid-1 text-n-slate-11"
                        >
                          <Icon
                            icon="i-lucide-heart"
                            class="size-2.5 text-n-ruby-9 fill-current"
                          />
                          {{ reply.likeCount }}
                        </span>
                      </div>

                      <!-- The page's own replies need deleting too: a duplicate or a
                           reply sent to the wrong thread is only removable from here. -->
                      <div v-if="!isDeleted(reply)" class="relative shrink-0">
                        <button
                          type="button"
                          data-testid="reply-menu-toggle"
                          :aria-label="t('COMMENTS.ACTIONS.MORE')"
                          :aria-expanded="openMenuId === reply.id"
                          class="grid mt-1 rounded-full opacity-0 size-7 text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 place-items-center group-hover:opacity-100 focus-visible:opacity-100"
                          :class="{ 'opacity-100': openMenuId === reply.id }"
                          @click="toggleMenu(reply)"
                        >
                          <Icon
                            icon="i-lucide-more-horizontal"
                            class="size-4"
                          />
                        </button>
                        <div
                          v-if="openMenuId === reply.id"
                          class="absolute right-0 z-20 flex flex-col w-40 py-1 border shadow-lg top-9 rounded-xl border-n-weak bg-n-solid-1"
                        >
                          <button
                            type="button"
                            data-testid="reply-delete-button"
                            :disabled="uiFlags.deletingItem"
                            class="flex items-center gap-2 px-3 py-2 text-sm text-left text-n-ruby-9 hover:bg-n-alpha-1 disabled:opacity-50"
                            @click="
                              closeMenu();
                              openDeleteDialog(reply);
                            "
                          >
                            <Icon icon="i-lucide-trash-2" class="size-3.5" />
                            {{ t('COMMENTS.ACTIONS.DELETE') }}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="!isDeleted(reply)"
                      class="flex items-center gap-3 pt-1 pl-3 text-[12px] font-semibold text-n-slate-11"
                    >
                      <button
                        type="button"
                        data-testid="reply-like-button"
                        :disabled="uiFlags.liking || uiFlags.unliking"
                        :aria-pressed="reply.likedByPage"
                        class="hover:underline disabled:opacity-50"
                        :class="{ 'text-n-ruby-9': reply.likedByPage }"
                        @click="toggleLike(reply)"
                      >
                        {{ t('COMMENTS.ACTIONS.LIKE') }}
                      </button>
                      <button
                        type="button"
                        data-testid="reply-reply-toggle"
                        class="hover:underline"
                        @click="
                          replyingId === reply.id
                            ? cancelReply()
                            : startReply(reply)
                        "
                      >
                        {{ t('COMMENTS.REPLY_BOX.REPLY_BUTTON') }}
                      </button>
                      <span class="font-normal text-n-slate-10">
                        {{ relativeTime(reply.commentedAt) }}
                      </span>
                    </div>

                    <div v-if="replyingId === reply.id" class="flex gap-2 mt-2">
                      <input
                        v-model="replyText"
                        type="text"
                        data-testid="post-detail-reply-input"
                        :placeholder="
                          t('COMMENTS.POSTS.THREAD.REPLY_PLACEHOLDER', {
                            page: post.pageName,
                          })
                        "
                        class="flex-1 h-9 px-4 text-sm rounded-full bg-n-alpha-2 text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                        @keydown.enter="sendReply(reply)"
                      />
                      <Button
                        data-testid="post-detail-reply-send"
                        size="sm"
                        :label="t('COMMENTS.REPLY_BOX.REPLY_BUTTON')"
                        :disabled="!replyText.trim() || isSendingReply"
                        :is-loading="isSendingReply"
                        @click="sendReply(reply)"
                      />
                    </div>
                  </div>
                </div>

                <div v-if="replyingId === comment.id" class="flex gap-2 mt-2">
                  <input
                    v-model="replyText"
                    type="text"
                    data-testid="post-detail-reply-input"
                    :placeholder="
                      t('COMMENTS.POSTS.THREAD.REPLY_PLACEHOLDER', {
                        page: post.pageName,
                      })
                    "
                    class="flex-1 h-9 px-4 text-sm rounded-full bg-n-alpha-2 text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                    @keydown.enter="sendReply(comment)"
                  />
                  <Button
                    data-testid="post-detail-reply-send"
                    size="sm"
                    :label="t('COMMENTS.REPLY_BOX.REPLY_BUTTON')"
                    :disabled="!replyText.trim() || isSendingReply"
                    :is-loading="isSendingReply"
                    @click="sendReply(comment)"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- Catches the click that dismisses an open `...` menu. Sits under the menu itself
         so choosing an item still lands on the item. -->
    <div v-if="openMenuId" class="fixed inset-0 z-10" @click="closeMenu" />

    <Dialog
      ref="cancelDialogRef"
      type="alert"
      :title="t('COMMENTS.POSTS.CANCEL_DIALOG.TITLE')"
      :description="t('COMMENTS.POSTS.CANCEL_DIALOG.DESCRIPTION')"
      :confirm-button-label="t('COMMENTS.POSTS.CANCEL_DIALOG.CONFIRM')"
      @confirm="confirmCancel"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('COMMENTS.ACTIONS.DELETE_DIALOG.TITLE')"
      :description="t('COMMENTS.ACTIONS.DELETE_DIALOG.DESCRIPTION')"
      :confirm-button-label="t('COMMENTS.ACTIONS.DELETE_DIALOG.CONFIRM')"
      @confirm="confirmDelete"
    />
  </div>
</template>
